import { TableComponent } from '@atoms';
import { useDebounce } from '@hooks';
import { Col, DatePicker, Input, Row, Select } from 'antd';
import { Dayjs } from 'dayjs';
import { memo, useCallback, useMemo, useState } from 'react';

import { dateFormatted } from 'src/app/helpers/DateHelper';

import { useProgramList } from '../hooks/useProgram';
import { ProgramStatus } from '../types/program';
import { SimpleProgram } from '../types/simpleProgram';
import { ProgramNameWithStatus } from './ProgramNameWithStatus';
import { ProgramsTableActions } from './ProgramsTableActions';

const { Option } = Select;

export const ListProgramsTable = memo(() => {
  const [filter, setFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState<ProgramStatus | undefined>(
    undefined
  );
  const [createdAtFilter, setCreatedAtFilter] = useState<string | undefined>(
    undefined
  );
  const debouncedFilter = useDebounce(filter ?? '', 900);
  const [page, setPage] = useState<number>(() => 1);
  const [pageLimit, setPageLimit] = useState<number>(() => 10);

  const { programs, isLoading, totalCount } = useProgramList({
    page,
    limit: pageLimit,
    text: debouncedFilter,
    status: statusFilter,
    createdAt: createdAtFilter,
  });

  const tableColumns = useMemo(() => {
    return [
      {
        title: 'Programa',
        dataIndex: 'name',
        render: (name: string, record: SimpleProgram) => (
          <ProgramNameWithStatus
            name={name}
            status={record.status}
            programId={record.id}
          />
        ),
        sorter: (a: SimpleProgram, b: SimpleProgram) =>
          a.name.localeCompare(b.name),
      },
      {
        title: 'Treinador(a)',
        dataIndex: 'instructors',
        width: 240,
        sorter: (a: SimpleProgram, b: SimpleProgram) => {
          const instructorA = a.instructors?.[a.instructors.length - 1] || '';
          const instructorB = b.instructors?.[b.instructors.length - 1] || '';
          return instructorA.localeCompare(instructorB);
        },
        render: (instructors: SimpleProgram['instructors']) =>
          instructors?.[0] ?? 'Não definido',
      },
      {
        title: 'Criado em',
        dataIndex: 'createdAt',
        width: 120,
        render: (createdAt: SimpleProgram['createdAt']) => {
          return dateFormatted(createdAt, false);
        },
      },
      {
        title: 'Ações',
        dataIndex: 'actions',
        width: 64,
        align: 'center' as const,
        render: (_: any, rowData: SimpleProgram) => (
          <ProgramsTableActions
            programId={rowData.id}
            programName={rowData.name}
            status={rowData.status}
          />
        ),
      },
    ];
  }, []);

  const handleChangePage = useCallback(
    (selectedPage: number, paginationSize: number | undefined) => {
      if (selectedPage !== page) setPage(selectedPage);
      if (paginationSize && paginationSize !== pageLimit)
        setPageLimit(paginationSize);
    },
    [page, pageLimit]
  );

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFilter(event.target.value);
    setPage(1);
  };

  const handleStatusChange = (value: ProgramStatus) => {
    setStatusFilter(value);
    setPage(1);
  };

  const handleDateChange = (date: Dayjs | null) => {
    setCreatedAtFilter(date?.format('YYYY-MM-DD') || undefined);
    setPage(1);
  };

  return (
    <>
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col span={18}>
          <Input.Search
            value={filter}
            onChange={handleInputChange}
            placeholder='Buscar programa ou treinador'
            style={{ width: '100%' }}
            allowClear
          />
        </Col>
        <Col span={3}>
          <Select
            value={statusFilter}
            onChange={handleStatusChange}
            placeholder='Status'
            style={{ width: '100%' }}
            allowClear
          >
            <Option value={ProgramStatus.Active}>Ativo</Option>
            <Option value={ProgramStatus.Inactive}>Inativo</Option>
            <Option value={ProgramStatus.Unpublished}>Não publicado</Option>
            <Option value={ProgramStatus.Scheduled}>Programado</Option>
            <Option value={ProgramStatus.Incompleted}>Incompleto</Option>
            <Option value={ProgramStatus.Completed}>Completo</Option>
          </Select>
        </Col>
        <Col span={3}>
          <DatePicker
            onChange={handleDateChange}
            format='DD/MM/YYYY'
            style={{ width: '100%' }}
            placeholder='Criado em'
          />
        </Col>
      </Row>

      <TableComponent
        pageSize={pageLimit}
        columns={tableColumns}
        data={programs}
        total={totalCount}
        loading={isLoading}
        currentPage={page}
        onChangePage={(selectedPage, paginationPageSize) =>
          handleChangePage(selectedPage, paginationPageSize)
        }
        hidePaginationOnSinglePage={false}
        size='default'
      />
    </>
  );
});
